import PropTypes from 'prop-types';
import { useState, createContext, useContext, useMemo, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';

// ----------------------------------------------------------------------

const SnackbarContext = createContext(null);

export function useSnackbar() {
    const context = useContext(SnackbarContext);

    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }

    return context;
}

SnackbarProvider.propTypes = {
    children: PropTypes.node,
};

export default function SnackbarProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [variant, setVariant] = useState('info'); // info, success, warning, error

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const enqueueSnackbar = useCallback((newMessage, options = {}) => {
        setMessage(newMessage);
        setVariant(options.variant || 'info');
        setOpen(true);
    }, []);

    const value = useMemo(
        () => ({
            enqueueSnackbar,
        }),
        [enqueueSnackbar]
    );

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={3000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleClose} severity={variant} sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
}
